import React from 'react'
import PropTypes from 'prop-types'
import { createComponent } from 'react-fela'
import Icon from 'design-framework/common/ui/Icon'
import tickIcon from 'design-framework/common/icons/valid-field-tick.svg'
import clearIcon from 'design-framework/common/icons/clear-field-text.svg'
import errorIcon from 'design-framework/common/icons/input-error.svg'
import loadingSpinner from 'design-framework/common/icons/loading-spinner.svg'
import TextArea from './components/TextArea'

import {
	__input,
	__label,
	__bar,
	__wrapper,
	__placeholder,
	__addon,
	__text,
	__SvgSize,
	__clear,
} from './__style'

const Input = createComponent(__input, 'input', [
	'type',
	'placeholder',
	'icon',
	'value',
	'required',
	'readOnly',
	'label',
	'name',
	'onFocus',
	'onBlur',
	'onKeyDown',
	'onChange',
	'disabled',
	'data-auto',
	'autoComplete',
	'maxlength',
	'tabIndex',
])

const Bar = createComponent(__bar, 'span')
const Wrapper = createComponent(__wrapper, 'div')
const Label = createComponent(__label, 'label')
const PlaceHolder = createComponent(__placeholder, 'div')
const ClearButton = createComponent(__clear)
const Addon = createComponent(__addon)
const Text = createComponent(__text)
const SvgSize = createComponent(__SvgSize)

class TextField extends React.Component {
	constructor(props) {
		super(props)
		// Use initial prop to set state value
		this.state = {
			inputValue: props.value || '',
			isActive: false,
			hasData: !!props.value,
			hasError: false,
			errorText: '',
			isLoading: false,
		}
	}

	static getDerivedStateFromProps(props, state) {
		if (props.renderValueViaProp && props.value !== state.inputValue) {
			let hasError = false
			const { pattern, isRequired, value } = props
			const regexPattern = new RegExp(pattern)
			if (value === '' && isRequired) {
				hasError = true
			}
			if (!regexPattern.test(props.value)) {
				hasError = true
			}

			return {
				inputValue: value,
				hasData: value !== '',
				hasError,
			}
		}

		return null
	}

	onFocusFunction = e => {
		// Used to hide all clear field icons
		Array.from(document.querySelectorAll('.btn')).forEach(item => {
			item.style.display = // eslint-disable-line no-param-reassign
				e.target.value &&
				item.childNodes[0].dataset.auto === `clear-${e.target.name}`
					? 'block'
					: 'none'
		})

		if (!this.props.readOnly) {
			this.setState({
				isActive: true,
			})
		}
		if (this.props.onFocus) {
			this.props.onFocus()
		}
	}

	onBlurFunction = e => {
		e.persist()
		if (!this.state.inputValue) {
			if (this.props.isRequired) {
				this.setState({
					hasError: true,
					errorText: 'Cannot be Empty',
				})
			}
			this.setState({
				isActive: false,
				hasData: false,
			})
		} else {
			this.validateRegex()
			this.setState({
				isActive: false,
				hasData: true,
			})

			const clearBtn = document.querySelector(
				`[data-auto=clear-${e.target.name}]`
			)
			// This will hide the clear button after losing focus
			if (clearBtn) {
				setTimeout(() => {
					clearBtn.parentElement.style.display = 'none'
				}, 250)
			}
		}
		this.props.onBlur()
	}

	onKeyDownFunction = e => {
		const exceptSpecialSymbols = [
			'e',
			'E',
			'+',
			'-',
			'.',
			'ArrowDown',
			'ArrowUp',
		]
		if (
			e.target.type === 'number' &&
			exceptSpecialSymbols.includes(e.key)
		) {
			e.preventDefault()
		}
	}

	validateRegex = () => {
		const patt = new RegExp(this.props.pattern)
		const result = patt.test(this.state.inputValue)
		this.setState({
			hasError: !result,
			errorText:
				this.props.regexErrorMsg ||
				this.props.errorMsg ||
				'Invalid Input',
		})
	}

	handleChange = e => {
		e.persist()

		const {
			isPhoneNumber,
			label,
			name,
			formErrors = {},
			updateErrors,
			isPrice,
		} = this.props

		// Show Clear button if there is some text present in textfield
		const clearBtn = document.querySelector(
			`[data-auto=clear-${e.target.name}]`
		)
		if (clearBtn)
			clearBtn.parentElement.style.display = e.target.value
				? 'block'
				: 'none'

		const numberPattern = /^[0-9]*$/
		const pricePattern = /^\d+(.\d{1,2})?$/
		if (isPhoneNumber && !numberPattern.test(e.target.value)) return
		if (isPrice && e.target.value && !pricePattern.test(e.target.value))
			return
		this.setState({
			inputValue: e.target.value,
			hasError: false,
			hasData: e.target.value !== '',
		})
		if (this.props.onChange) {
			this.props.onChange(e)
		} else if (this.props.handleChange) {
			this.props.handleChange({
				label,
				name,
				text: e.target.value,
			})
		}

		const { errorArr = [] } = formErrors

		if (errorArr.length) {
			updateErrors({
				focusOnField: false,
				errorArr: formErrors.errorArr.filter(
					item => item.fieldName !== name
				),
			})
		}
	}

	clearInput = event => {
		const { label, name, isRequired } = this.props

		this.setState({
			inputValue: '',
			hasData: false,
		})

		if (isRequired) {
			this.setState({
				hasError: true,
				errorText: 'Cannot be Empty',
			})
			this.props.handleChange({
				label,
				name,
				text: '',
			})
		}

		Array.from(document.querySelectorAll('.btn')).forEach(item => {
			item.style.display = 'none' // eslint-disable-line no-param-reassign
		})

		// Bring back the focus after clearing field
		document
			.querySelector(
				`[data-auto=field-${event.target.dataset.auto.slice(6)}]`
			)
			.focus()
	}

	render() {
		const {
			type,
			readOnly,
			disabled,
			fullWidth,
			multiLine,
			rows,
			rowsMax,
			label = '',
			addon,
			placeholder,
			description,
			errorText,
			successText,
			autoComplete,
			name: propsName,
			style,
			formErrors = {},
			updateErrors = () => {},
			inputStyles = {},
			hideClearIcon = false,
			disableHightLightField = false,
			hideTickIcon = false,
			maxlength,
			tabIndex,
		} = this.props

		const { idSelector = label.trim().replace(/\s/g, '-') } = this.props

		const {
			inputValue,
			isActive,
			hasData,
			hasError: stateHasError,
			errorText: stateErrorText,
			isLoading = false,
		} = this.state

		const passDownProps = {
			type,
			readOnly,
			hasError: stateHasError || !!errorText,
			value: inputValue,
			onFocus: this.onFocusFunction,
			onChange: this.handleChange,
			onBlur: this.onBlurFunction,
			onKeyDown: this.onKeyDownFunction,
			name: propsName,
			disabled,
			'data-id': idSelector,
			'data-auto': `field-${propsName}`,
			autoComplete,
			style: inputStyles,
			maxlength,
			tabIndex,
		}

		const clearButtonProps = {
			'data-auto': `clear-${propsName}`,
		}

		let formSubmissionErrorText = ''

		const { errorArr = [], focusOnField = false } = formErrors

		if (errorArr.length) {
			const errorIndex = errorArr.findIndex(
				errorItem => errorItem.fieldName === propsName
			)

			formSubmissionErrorText = errorArr[errorIndex]
				? errorArr[errorIndex].message
				: ''

			if (errorIndex === 0 && formSubmissionErrorText && focusOnField) {
				const elem = document.querySelector(
					`[data-auto=field-${propsName}]`
				)
				const { y = 0 } = elem.getBoundingClientRect()
				window.scrollTo({
					top: y,
					left: 0,
					behavior: 'smooth',
				})
				updateErrors({
					focusOnField: false,
					errorArr,
				})
			}
		}

		return (
			<Wrapper fullWidth={fullWidth} style={style}>
				<div style={{ width: '100%' }}>
					{multiLine && (
						<div
							data-auto={`field-${propsName}`}
							style={{ position: 'relative', zIndex: 1 }}
						>
							<TextArea
								rowsMax={rowsMax}
								rows={rows}
								{...passDownProps}
							/>
						</div>
					)}
					{!multiLine && (
						<div style={{ display: 'flex' }}>
							<Input {...passDownProps} />
							{addon && <Addon {...passDownProps}>{addon}</Addon>}
						</div>
					)}

					<Bar
						isActive={isActive}
						hasError={stateHasError}
						readOnly={readOnly}
						disableBackground={disableHightLightField}
					/>
					<Label
						isActive={isActive}
						hasData={hasData}
						labelLength={label.length}
					>
						{label}
					</Label>
					{((isActive && !hasData) || (!label && !hasData)) && (
						<PlaceHolder rows={rows} multiLine={multiLine}>
							{placeholder}
						</PlaceHolder>
					)}
					{description && <Text>{description}</Text>}
					{stateHasError && !errorText && !formSubmissionErrorText && (
						<Text hasError>
							<Icon width="14" height="14" glyph={errorIcon} />
							{stateErrorText}
						</Text>
					)}
					{errorText && !formSubmissionErrorText && (
						<Text hasError>
							<Icon width="14" height="14" glyph={errorIcon} />
							{errorText}
						</Text>
					)}
					{formSubmissionErrorText && hasData && (
						<Text hasError>
							<Icon width="14" height="14" glyph={errorIcon} />
							{formSubmissionErrorText}
						</Text>
					)}
					{successText && <Text>{successText}</Text>}

					{/* if addon prop is supplied don't show these icons. */}
					{!addon && (
						<>
							{!readOnly &&
								!isActive &&
								hasData &&
								!stateHasError &&
								!errorText &&
								!formSubmissionErrorText &&
								!hideTickIcon && (
									<SvgSize absolute top={8}>
										<Icon
											width="24"
											height="24"
											glyph={tickIcon}
										/>
									</SvgSize>
								)}

							{!readOnly && isLoading && (
								<SvgSize absolute isLoader top={12} right={4}>
									<Icon
										width="12"
										height="12"
										glyph={loadingSpinner}
									/>
								</SvgSize>
							)}

							{!readOnly && !hideClearIcon && (
								<ClearButton className="btn">
									<button
										tabIndex="-1"
										{...clearButtonProps}
										type="button"
										onClick={this.clearInput}
									>
										<Icon
											style={{ pointerEvents: 'none' }}
											width="10"
											height="10"
											glyph={clearIcon}
										/>
									</button>
								</ClearButton>
							)}
						</>
					)}
				</div>
			</Wrapper>
		)
	}
}

TextField.propTypes = {
	/** Type attribute for the input element */
	type: PropTypes.string,
	/** Label for the input element */
	label: PropTypes.string,
	/** Icon on the left of input element */
	icon: PropTypes.object,
	/** Required attribute of input element */
	required: PropTypes.bool,
	/** Readonly attribute of input element */
	readOnly: PropTypes.bool,
	/** Value attribute of input element */
	handleChange: PropTypes.func,
	/** Regex pattern matching for the element */
	pattern: PropTypes.string,
	/** Error Message to be played incase of wrong input(not matching the Regex) */
	regexErrorMsg: PropTypes.string,
	/** Required attribute of the input element. States if a input is compulsory or not */
	isRequired: PropTypes.bool,
	/** HTML name attribute for the input element */
	name: PropTypes.string.isRequired,
	/** Value attribute of input element */
	value: PropTypes.string,
	/** Placeholder attribute of input element */
	placeholder: PropTypes.string,
	/** A react component to show on the right of input field */
	addon: PropTypes.node,
	/** Error msg to be displayed */
	errorText: PropTypes.string,
	/** Success msg to be displayed */
	successText: PropTypes.string,
	/** MultiLine support */
	multiLine: PropTypes.bool,
	/** minimum number of Rows for MultiLine support */
	rows: PropTypes.number,
	/** to use the parent elements width */
	fullWidth: PropTypes.bool,
	'data-auto': PropTypes.string,
	/** Proveide maximum character limit to input fields */
	maxlength: PropTypes.string,
	/** On Losing focus call back function */
	onBlur: PropTypes.func,
	/** Maintain state via prop */
	renderValueViaProp: PropTypes.bool,
	// Function to be invoked on focus
	onFocus: PropTypes.func,
}

TextField.defaultProps = {
	required: false,
	readOnly: false,
	value: '',
	label: '',
	pattern: '',
	regexErrorMsg: '',
	errorText: '',
	placeholder: '',
	handleChange: () => {},
	onBlur: () => {},
	isRequired: false,
	multiLine: false,
	rows: 1,
	fullWidth: false,
	renderValueViaProp: false,
	onFocus: () => {},
}

export default TextField
